import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import debounce from 'lodash/debounce';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Button, ButtonIcon, IconChip } from '@app/core';
import { API_URL, ParticipationStatus } from '@app/api';
import { SearchInput } from '@app/form';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ColumnVisibilitySelect } from './ColumnVisibilitySelect';
import { FieldConfig } from 'pages/Dashboard/Sessions/Details/hooks/useFieldConfig';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase * 3,
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: theme.borderRadius.std,
    borderTopRightRadius: theme.borderRadius.std,
    border: `1px solid ${theme.colors.grayBorder}`,
    borderBottom: 'none',
    padding: theme.marginBase,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  globalSearch: {
    width: theme.marginBase * 50,
  },
  globalActionsRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  globalActionButton: {
    display: 'flex',
    gap: theme.marginBase,
  },
  globalActionButtonIcon: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
    fontSize: theme.iconSize.large,
  },
  filterBlock: {
    display: 'flex',
    gap: 0,
  },
  filterResultCount: {
    paddingRight: theme.marginBase / 4,
    borderTopRightRadius: 'unset',
    borderBottomRightRadius: 'unset',
  },
  filterClearButton: {
    borderTopLeftRadius: 'unset',
    borderBottomLeftRadius: 'unset',
  },
}));

interface Props {
  searchResultTotal?: number;
  sessionId: string;
  statusList: ParticipationStatus[];
  onSearch: (search: string) => any;
  onClearFilters: () => void;
  search?: string;
  fieldConfigs: FieldConfig[];
  hiddenColumns: string[];
  onHiddenColumnsChange: (v: string[]) => any;
  onClearColumns?: () => string[];
}

export const ActionBar = ({
  searchResultTotal,
  sessionId,
  statusList,
  onSearch,
  search = '',
  fieldConfigs,
  hiddenColumns,
  onHiddenColumnsChange,
  onClearColumns,
  onClearFilters,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState(search);

  const onDownloadClick = useCallback(() => {
    const statusParams = statusList.map((s) => `status[]=${s}`).join('&');

    window.open(
      `${API_URL}/sessions/${sessionId}/participations/csv?timezone=${moment.tz.guess()}&${statusParams}`,
      '_blank',
    );
  }, [sessionId, statusList]);

  const debouncedSearch = useMemo(() => {
    return debounce((value: string) => {
      if (value.length >= 2 || value.length === 0) {
        onSearch(value);
      }
    }, 500);
  }, [onSearch]);

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  useEffect(() => {
    if (search) {
      return;
    }
    if (searchInput) {
      setSearchInput('');
    }
  }, [search, setSearchInput]);

  return (
    <div className={classes.container}>
      <SearchInput
        containerClass={classes.globalSearch}
        type="text"
        placeholder={t('sessions.details.participationsDetails.global.search')}
        onChange={onSearchChange}
        value={searchInput}
        onClear={() => onSearch('')}
      />
      {searchResultTotal !== undefined && (
        <div className={classes.filterBlock}>
          <IconChip className={classes.filterResultCount} icon="ri-group-line">
            {searchResultTotal}
          </IconChip>
          <ButtonIcon
            className={classes.filterClearButton}
            icon="ri-close-line"
            onClick={onClearFilters}
            color="blue"
            colorStyle="muted"
          />
        </div>
      )}
      <div className={classes.globalActionsRight}>
        <Button
          small
          color="blue"
          variant="transparent"
          contentClassName={classes.globalActionButton}
          onClick={onDownloadClick}
        >
          <i
            className={classnames(
              'ri-download-line',
              classes.globalActionButtonIcon,
            )}
          />
          {t('sessions.details.participationsDetails.global.download')}
        </Button>
        <ColumnVisibilitySelect
          fieldConfigs={fieldConfigs}
          hiddenColumns={hiddenColumns}
          onHiddenColumnsChange={onHiddenColumnsChange}
          onClear={onClearColumns}
        />
      </div>
    </div>
  );
};
